import _ListenWriteSubmitMutation from "../__generated__/ListenWriteSubmitMutation.graphql";
import _ListenWriteQuery from "../__generated__/ListenWriteQuery.graphql";
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Square, Volume2, X } from 'lucide-react';
import { claim } from '../audio/oneVoice.ts';
import { manyThenReplay } from '../audio/replay.ts';
import { En } from '../i18n/En';
import { T } from '../i18n/RichText';
import { msg, t, type Message } from '../i18n/t';
import { attemptOf, canCheck, canPlay, canRetry, check, FRESH, MAX_PLAYS, pairMisheard, played, retry, typed, type Take } from '../listen/dictation.ts';
import { clearDraft, readDraft, writeDraft } from '../listen/draft.ts';
import { soundCopy, submitCopy } from '../listen/feedback.ts';
import { canSubmit, clipKey, dictated, guideRange, MAX_CHARS, MIN_WORDS, sentencesOf, wordCount, type PassageSentence } from '../listen/passage.ts';
import { SentencePlayer, type Clip } from '../listen/player.ts';
import { canAdvance, canGoBack, nextStep, previousStep, STEPS, stepNumber, type Step } from '../listen/steps.ts';
import { registerFor } from '../profile/register';
import { codeFromError } from './aidErrors.ts';
import { Speak } from './AidsPanel';
import { RetellingFeedback } from './RetellingFeedback.tsx';
import type { AidsPanelSpeakMutation } from '../__generated__/AidsPanelSpeakMutation.graphql';
import type { ListenWriteQuery as ListenWriteQueryType } from '../__generated__/ListenWriteQuery.graphql';
import type { ListenWriteSubmitMutation } from '../__generated__/ListenWriteSubmitMutation.graphql';
const ListenWriteQuery = (_ListenWriteQuery.hash && _ListenWriteQuery.hash !== "b25f8af5fa3fb17b8450c2bd372d7ae9" && console.error("The definition of 'ListenWriteQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ListenWriteQuery);
const Submit = (_ListenWriteSubmitMutation.hash && _ListenWriteSubmitMutation.hash !== "fe1b3d4dc73c9db55aee70ffb145db91" && console.error("The definition of 'ListenWriteSubmitMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _ListenWriteSubmitMutation);
type Pages = NonNullable<NonNullable<NonNullable<ListenWriteQueryType['response']['me']>['learner']>['storyBook']>['pages'];
type Passage = NonNullable<Pages[number]['listenPassage']>;
const STEP_NAME = {
  listen: 'listen-step-listen',
  transcript: 'listen-step-transcript',
  dictation: 'listen-step-dictation',
  write: 'listen-step-write',
  feedback: 'listen-step-feedback'
} as const;

/**
 * Listen & Write (listen & write design §3), at
 * `/books/:bookId/listen/:firstPage`. Adult only: the kid register is sent to
 * the shelf and nothing of the activity is drawn, since it shows a learner
 * their mistakes by design (§1, "Who"). The server agrees independently — an
 * adult book is null in the kid register, and a tale offers no passage.
 *
 * `?retelling=` opens one retelling's feedback (§7's history link, and the
 * page a hand-in lands on), so a reload while the feedback is being written
 * comes back to it rather than to an empty write box.
 */
export function ListenWrite() {
  const {
    bookId = '',
    firstPage = ''
  } = useParams();
  const [params, setParams] = useSearchParams();
  const data = useLazyLoadQuery<ListenWriteQueryType>(ListenWriteQuery, {
    bookId
  });
  const learner = data.me?.learner;
  if (registerFor(learner?.defaultCharacter?.audience) === 'kid') return <Navigate to="/books" replace />;
  const book = learner?.storyBook;
  if (!learner || !book) return <p className="text-base-content/50 py-12 text-center">{t('books-gone')}</p>;
  const retelling = params.get('retelling');
  if (retelling) {
    return <Frame bookId={book.id} step="feedback">
        <RetellingFeedback id={retelling} bookId={book.id} pages={book.pages} />
      </Frame>;
  }
  const passage = book.pages.find(p => String(p.number) === firstPage)?.listenPassage;
  if (!passage) return <p className="text-base-content/50 py-12 text-center">{t('listen-gone')}</p>;
  return <Activity key={`${book.id}:${passage.firstPage}`} learnerId={learner.id} bookId={book.id} passage={passage} level={learner.cefrLevel}
  // Replace: Back from the feedback must not reopen an empty activity
  // whose draft is gone.
  onSubmitted={id => setParams({
    retelling: id
  }, {
    replace: true
  })} />;
}

/** The close link and the stepper, shared by the four steps and the feedback screen. */
function Frame({
  bookId,
  step,
  children
}: {
  bookId: string;
  step: Step;
  children: ReactNode;
}) {
  const n = stepNumber(step);
  return <div className="mx-auto flex w-full max-w-lg flex-col gap-5 p-4">
      <div className="flex items-center gap-2">
        <Link to={`/books/${bookId}`} className="btn btn-ghost min-h-(--hit-min) min-w-(--hit-min)" aria-label={t('listen-close')}>
          <X aria-hidden />
        </Link>
        <h1 className="text-xl font-semibold">{t('listen-entry')}</h1>
      </div>
      <p className="sr-only" aria-live="polite">
        {t('listen-step-of', {
        step: n,
        name: t(STEP_NAME[step])
      })}
      </p>
      {/* `min-w-0`: daisyUI gives each step 4rem, and five of those are wider
          than a 375 px phone's column, so the last label was cut off and the
          stepper scrolled sideways (`just listen-probe`). */}
      <ol className="steps steps-horizontal w-full text-xs" aria-hidden>
        {STEPS.map((s, i) => <li key={s} className={`step min-w-0${i < n ? ' step-primary' : ''}`}>
            {t(STEP_NAME[s])}
          </li>)}
      </ol>
      {children}
    </div>;
}

/**
 * The four steps before feedback. All state lives here, so stepping back and
 * forth keeps the dictation and the draft, and it is mirrored to the device
 * (`listen/draft.ts`) so a reload keeps them too; it is gone once handed in.
 */
function Activity({
  learnerId,
  bookId,
  passage,
  level,
  onSubmitted
}: {
  learnerId: string;
  bookId: string;
  passage: Passage;
  level: string;
  onSubmitted: (id: string) => void;
}) {
  const [step, setStep] = useState<Step>('listen');
  const sentences = useMemo(() => sentencesOf(passage.pages), [passage]);
  const dictation = useMemo(() => dictated(passage.pages, passage.dictation), [passage]);
  const place = useMemo(() => ({
    learnerId,
    bookId,
    firstPage: passage.firstPage
  }), [learnerId, bookId, passage.firstPage]);
  const [saved] = useState(() => readDraft(place, dictation.map(d => d.text)));
  const [takes, setTakes] = useState<Take[]>(() => saved?.takes ?? dictation.map(() => FRESH));
  const [text, setText] = useState(saved?.text ?? '');
  useEffect(() => writeDraft(place, {
    text,
    takes
  }), [place, text, takes]);
  const [submitError, setSubmitError] = useState<Message | null>(null);
  const [submit, submitting] = useMutation<ListenWriteSubmitMutation>(Submit);
  const audio = usePassageAudio(sentences);
  const go = (to: Step | null) => {
    if (!to) return;
    audio.stop();
    setStep(to);
  };
  const handIn = () => {
    if (!canSubmit(text) || submitting) return;
    setSubmitError(null);
    submit({
      variables: {
        input: {
          storyPageId: passage.pages[0].id,
          text: text.trim(),
          dictation: dictation.flatMap((d, i) => attemptOf(d, takes[i]) ?? [])
        }
      },
      onCompleted: ({
        submitRetelling
      }) => {
        clearDraft(place);
        onSubmitted(submitRetelling.retelling.id);
      },
      onError: err => setSubmitError(submitCopy(codeFromError(err)))
    });
  };
  return <Frame bookId={bookId} step={step}>
      {step === 'listen' && <>
          <p>{t('listen-listen-intro')}</p>
          <PlayAll audio={audio} slow={false} />
        </>}
      {step === 'transcript' && <Transcript audio={audio} sentences={sentences} />}
      {step === 'dictation' && <Dictation audio={audio} sentences={sentences} items={dictation} takes={takes} onTake={(i, change) => setTakes(all => all.map((take, j) => j === i ? change(take) : take))} />}
      {step === 'write' && <Write text={text} onText={setText} level={level} busy={submitting} error={submitError} onSubmit={handIn} />}
      {audio.error && <p role="alert" className="text-error text-sm">
          {t(audio.error)}
        </p>}
      <div className="grid w-full grid-cols-2 gap-3">
        {canGoBack(step) ? <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" onClick={() => go(previousStep(step))}>
            <ChevronLeft aria-hidden />
            {t('books-back')}
          </button> : <span />}
        {canAdvance(step) && <button type="button" className="btn btn-primary min-h-(--hit-min) gap-2" onClick={() => go(nextStep(step))}>
            {t('books-next')}
            <ChevronRight aria-hidden />
          </button>}
      </div>
    </Frame>;
}
type PassageAudio = {
  playing: number | null;
  error: Message | null;
  slow: boolean;
  setSlow: (slow: boolean) => void;
  /** `onStart` runs once the clip starts, so a fetch that fails costs no dictation play. */
  playFrom: (i: number, slow: boolean, once?: boolean, onStart?: () => void) => void;
  stop: () => void;
};

/**
 * One `SentencePlayer` for the activity, over one clip cache keyed per
 * (page, sentence, slow) (§4), so a replay, the transcript step and the
 * dictation never synthesise a sentence twice at one speed.
 */
function usePassageAudio(sentences: readonly PassageSentence[]): PassageAudio {
  const [speak] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [playing, setPlaying] = useState<number | null>(null);
  const [error, setError] = useState<Message | null>(null);
  const [slow, setSlow] = useState(false);
  const clips = useRef(manyThenReplay<string>());
  const live = useRef(true);
  const onStart = useRef<(() => void) | null>(null);
  const player = useMemo(() => {
    // Read by `onError`: set by the fetch's own `onError`, which runs first.
    let code: string | null = null;
    return new SentencePlayer({
      count: sentences.length,
      load(i, slowly, use, failed) {
        const s = sentences[i];
        clips.current.get(clipKey(s, slowly), (store, fail) => speak({
          variables: {
            input: {
              storyPageId: s.pageId,
              sentence: s.sentence,
              slow: slowly
            }
          },
          onCompleted: ({
            speak: result
          }) => store(result.wavBase64),
          onError: err => {
            code = codeFromError(err);
            fail();
          }
        }), use, failed);
      },
      makeClip(wav) {
        if (!live.current) return null;
        const el = new Audio(`data:audio/wav;base64,${wav}`);
        if (!claim(el)) {
          // The player ends the run on a refused clip; without this the
          // learner's tap would simply do nothing.
          setError(msg('listen-voice-busy'));
          return null;
        }
        const clip: Clip = {
          play: () => el.play(),
          pause: () => el.pause(),
          onEnded: f => {
            el.onended = f;
          }
        };
        return clip;
      },
      onPlaying(i) {
        if (!live.current) return;
        setPlaying(i);
        if (i === null) return;
        setError(null);
        onStart.current?.();
        onStart.current = null;
      },
      onError() {
        if (live.current) setError(soundCopy(code));
      }
    });
  }, [sentences, speak]);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      player.stop();
    };
  }, [player]);
  return {
    playing,
    error,
    slow,
    setSlow,
    playFrom: (i, slowly, once, started) => {
      onStart.current = started ?? null;
      player.playFrom(i, slowly, once);
    },
    stop: () => {
      onStart.current = null;
      player.stop();
    }
  };
}
function PlayAll({
  audio,
  slow
}: {
  audio: PassageAudio;
  slow: boolean;
}) {
  const on = audio.playing !== null;
  return <button type="button" className="btn btn-primary btn-lg min-h-(--hit-min) gap-2 self-center" onClick={() => on ? audio.stop() : audio.playFrom(0, slow)}>
      {on ? <Square aria-hidden className="size-5" /> : <Volume2 aria-hidden className="size-5" />}
      {on ? t('listen-stop') : t('listen-play')}
    </button>;
}
function SlowToggle({
  audio
}: {
  audio: PassageAudio;
}) {
  return <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min) aria-pressed:btn-active" aria-pressed={audio.slow} onClick={() => {
    audio.stop();
    audio.setSlow(!audio.slow);
  }}>
      {t('listen-slower')}
    </button>;
}

/** §3.2: the text shown, the playing sentence highlighted, a tap replays one. */
function Transcript({
  audio,
  sentences
}: {
  audio: PassageAudio;
  sentences: readonly PassageSentence[];
}) {
  return <>
      <p>{t('listen-transcript-intro')}</p>
      <div className="flex items-center justify-between gap-2">
        <PlayAll audio={audio} slow={audio.slow} />
        <SlowToggle audio={audio} />
      </div>
      <p className="text-lg leading-relaxed">
        <En>
          {sentences.map((s, i) => <button key={`${s.pageId}:${s.sentence}`} type="button" className={`rounded px-0.5 text-start ${audio.playing === i ? 'bg-primary/20' : 'hover:bg-base-200'}`} aria-current={audio.playing === i || undefined} onClick={() => audio.playFrom(i, audio.slow, true)}>
                {s.text}
              </button>).flatMap((b, i) => i === 0 ? [b] : [' ', b])}
        </En>
      </p>
    </>;
}

/** §3.3. A sentence played from here is the passage's own clip, so the cache is shared. */
function Dictation({
  audio,
  sentences,
  items,
  takes,
  onTake
}: {
  audio: PassageAudio;
  sentences: readonly PassageSentence[];
  items: readonly PassageSentence[];
  takes: readonly Take[];
  onTake: (i: number, change: (take: Take) => Take) => void;
}) {
  return <>
      <div className="flex items-center justify-between gap-2">
        <p>{t('listen-dictation-intro')}</p>
        <SlowToggle audio={audio} />
      </div>
      {items.map((item, i) => {
      const take = takes[i];
      const at = sentences.findIndex(s => s.pageId === item.pageId && s.sentence === item.sentence);
      const inputId = `dictation-${i}`;
      const headingId = `dictation-${i}-heading`;
      return <section key={`${item.pageId}:${item.sentence}`} className="border-base-300 rounded-box flex flex-col gap-3 border p-3">
            <div className="flex items-center justify-between gap-2">
              <h2 id={headingId} className="font-semibold">
                {t('listen-dictation-sentence', {
              n: i + 1
            })}
              </h2>
              <button type="button" id={`${inputId}-play`} aria-labelledby={`${headingId} ${inputId}-play`} className="btn btn-outline btn-sm min-h-(--hit-min) gap-2" disabled={!canPlay(take) || at < 0} onClick={() => audio.playFrom(at, audio.slow, true, () => onTake(i, played))}>
                <Volume2 aria-hidden className="size-4" />
                {canPlay(take) ? t('listen-dictation-play', {
              left: MAX_PLAYS - take.plays
            }) : t('listen-dictation-no-plays')}
              </button>
            </div>
            <span id={`${inputId}-label`} className="sr-only">
              {t('listen-dictation-input')}
            </span>
            <textarea id={inputId} aria-labelledby={`${headingId} ${inputId}-label`} lang="en" dir="ltr" rows={2} autoCapitalize="off" spellCheck={false} className="textarea w-full" placeholder={t('listen-dictation-input')} value={take.typed} readOnly={take.result !== null} onChange={e => {
          const text = e.target.value;
          onTake(i, t0 => typed(t0, text));
        }} />
            {take.result === null ? <button type="button" className="btn btn-secondary btn-sm min-h-(--hit-min) self-start" disabled={!canCheck(take)} onClick={() => onTake(i, t0 => check(t0, item.text))}>
                {t('listen-check')}
              </button> : <>
                <DictationDiff take={take} />
                {canRetry(take) && <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min) self-start" onClick={() => onTake(i, retry)}>
                    {t('listen-try-again')}
                  </button>}
              </>}
          </section>;
    })}
    </>;
}

/**
 * The sentence and the learner's words side by side (§3.3), each word marked
 * by what happened to it. No score and no count (§3: nothing scored).
 *
 * A missed word is an `<ins>` (the learner's text lacks it) and an extra one
 * a `<del>`, and every marked word carries a visually hidden Fluent marker,
 * so a screen reader hears what the colour and underline say; the legend
 * under it is for the eye only. The marker sits outside `<En>`, in the
 * display language; only the word itself is read as English.
 */
function DictationDiff({
  take
}: {
  take: Take;
}) {
  if (!take.result) return null;
  const parts = pairMisheard(take.result);
  const missed = 'underline decoration-info decoration-2 underline-offset-4';
  const extra = 'line-through decoration-2 text-base-content/60';
  const misheard = 'underline decoration-warning decoration-wavy decoration-2 underline-offset-4';
  const sr = (text: string) => <span className="sr-only">{text} </span>;
  const target = parts.map((p, k) => {
    switch (p.kind) {
      case 'same':
        return <En key={k}>{p.text} </En>;
      case 'missed':
        return <Fragment key={k}>
            <ins className={missed}>
              {sr(t('listen-dictation-sr-missed'))}
              <En>{p.text}</En>
            </ins>{' '}
          </Fragment>;
      case 'misheard':
        return <Fragment key={k}>
            <span className={misheard}>
              {sr(t('listen-dictation-sr-misheard'))}
              <En>{p.target}</En>
            </span>{' '}
          </Fragment>;
      case 'extra':
        return null;
    }
  });
  const yours = parts.map((p, k) => {
    switch (p.kind) {
      case 'same':
        return <En key={k}>{p.text} </En>;
      case 'extra':
        return <Fragment key={k}>
            <del className={extra}>
              {sr(t('listen-dictation-sr-extra'))}
              <En>{p.text}</En>
            </del>{' '}
          </Fragment>;
      case 'misheard':
        return <Fragment key={k}>
            <span className={misheard}>
              <span className="sr-only">
                <T k="listen-dictation-sr-instead" params={{
                heard: p.heard,
                target: p.target
              }} parts={{
                en: w => <En>{w}</En>
              }} />{' '}
              </span>
              <En>
                <span aria-hidden>{p.heard}</span>
              </En>
            </span>{' '}
          </Fragment>;
      case 'missed':
        return null;
    }
  });
  return <div className="flex flex-col gap-2 text-sm">
      <div>
        <p className="text-base-content/60 text-xs">{t('listen-dictation-target')}</p>
        <p className="text-base">{target}</p>
      </div>
      <div>
        <p className="text-base-content/60 text-xs">{t('listen-dictation-yours')}</p>
        <p className="text-base">{yours}</p>
      </div>
      {take.result.same ? <p className="text-base-content/70">{t('listen-dictation-same')}</p> : <p className="text-base-content/60 flex flex-wrap gap-x-4 text-xs" aria-hidden>
          <span className={missed}>{t('listen-dictation-missed')}</span>
          <span className={extra}>{t('listen-dictation-extra')}</span>
          <span className={misheard}>{t('listen-dictation-misheard')}</span>
        </p>}
    </div>;
}

/**
 * §3.4: the transcript hidden, a soft guide shown as a range, and the button
 * opening from fifteen words. No live counter: a count would become the goal
 * (§1, non-goals), and nothing here turns red.
 */
function Write({
  text,
  onText,
  level,
  busy,
  error,
  onSubmit
}: {
  text: string;
  onText: (text: string) => void;
  level: string;
  busy: boolean;
  error: Message | null;
  onSubmit: () => void;
}) {
  const {
    min,
    max
  } = guideRange(level);
  const ready = canSubmit(text);
  const tooLong = Array.from(text.trim()).length > MAX_CHARS;
  return <>
      <p>{t('listen-write-intro')}</p>
      <label htmlFor="retelling" className="font-semibold">
        {t('listen-write-label')}
      </label>
      <textarea id="retelling" lang="en" dir="ltr" rows={10} className="textarea w-full text-base" value={text} onChange={e => onText(e.target.value)} />
      <p className="text-base-content/60 text-sm">{t('listen-write-guide', {
        min,
        max
      })}</p>
      {tooLong ? <p className="text-base-content/70 text-sm">{t('listen-write-too-long', {
        max: MAX_CHARS
      })}</p> : wordCount(text) < MIN_WORDS && <p className="text-base-content/60 text-sm">{t('listen-write-min', {
        min: MIN_WORDS
      })}</p>}
      <button type="button" className="btn btn-primary min-h-(--hit-min) gap-2" disabled={!ready || busy} onClick={onSubmit}>
        {busy && <Loader2 aria-hidden className="size-4 animate-spin" />}
        {busy ? t('listen-sending') : t('listen-submit')}
      </button>
      {error && <p role="alert" className="text-error text-sm">
          {t(error)}
        </p>}
    </>;
}